/**
 * The plans (PRD 13.2) and what each allows, checked in the worker and never
 * in the client.
 *
 * One table, so a change after customer conversations is one edit here. The
 * unit is the PRD's: headcount is the people the organisation has under
 * management (its Employees index, without those who have left), not seats
 * and not hires a month.
 *
 * Two rules sit above the table:
 *
 * - **An offboarding is never refused for a plan.** Taking somebody's access
 *   away is the time-critical, legal-risk half of the product (PRD 9); a
 *   termination blocked by a headcount limit is the failure utuwrk exists to
 *   prevent. Every limit here is on what an organisation adds.
 * - **Taking data away is never refused for a plan.** PRD 13.2 lists "full
 *   audit trail export" under Growth, but 13.4 promises every customer the
 *   audit log, the employees and the work history at any time, and 13.3's
 *   30-day window after cancelling depends on it. The narrower promise wins.
 * - **No plan, or one this code does not know, allows everything.** A stale
 *   or missing billing row grants rather than locks (illgati's direction
 *   note, decisions.md): the cost of a free month is smaller than the cost of
 *   a customer locked out of its own onboarding.
 */

export type PlanId = "free" | "starter" | "growth" | "business";

export const PLAN_IDS: readonly PlanId[] = ["free", "starter", "growth", "business"];

/** What a plan adds beyond the limits, by PRD 13.2's "key features". */
export type PlanFeature =
  /** More than one onboarding at a time. */
  | "simultaneous-work"
  /** A backup beside the person who does a department's cards. */
  | "backup-assignees"
  /** PRD 5.4's overdue dashboard and in-progress view. */
  | "dashboards"
  /** The HIPAA flag on a site, and the reviewer it asks for (PRD 11.2). */
  | "hipaa"
  /** More than one admin. */
  | "multi-admin";

export interface Plan {
  id: PlanId;
  name: string;
  /** US dollars a month. */
  price: number;
  /** People under management, not counting those who have left. */
  headcount: number;
  /** Null is no limit. */
  sites: number | null;
  /** The organisation's own templates, not retired; the starters are always there. Null is no limit. */
  templates: number | null;
  features: readonly PlanFeature[];
  /**
   * Mail the organisation may send in any hour, every kind together: its own
   * pool, so one busy organisation cannot use up another's (`mailAllowance()`).
   */
  mailPerHour: number;
}

const STARTER_FEATURES: readonly PlanFeature[] = ["simultaneous-work", "backup-assignees", "dashboards"];
const GROWTH_FEATURES: readonly PlanFeature[] = [...STARTER_FEATURES, "hipaa", "multi-admin"];

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: "free",
    name: "Free",
    price: 0,
    headcount: 10,
    sites: 1,
    templates: 1,
    features: [],
    mailPerHour: 50,
  },
  starter: {
    id: "starter",
    name: "Starter",
    price: 49,
    headcount: 75,
    sites: 3,
    templates: 3,
    features: STARTER_FEATURES,
    mailPerHour: 200,
  },
  growth: {
    id: "growth",
    name: "Growth",
    price: 99,
    headcount: 250,
    sites: null,
    templates: null,
    features: GROWTH_FEATURES,
    mailPerHour: 500,
  },
  business: {
    id: "business",
    name: "Business",
    price: 199,
    // PRD 13.2 says 500; raised to 1,000 (owner, 2026-10-12, decisions.md).
    headcount: 1000,
    sites: null,
    templates: null,
    features: GROWTH_FEATURES,
    mailPerHour: 1500,
  },
};

export const isPlanId = (value: unknown): value is PlanId => PLAN_IDS.includes(value as PlanId);

/** The plan a stored value names, or null for one this code does not know, which allows everything. */
export const planOf = (value: unknown): Plan | null => (isPlanId(value) ? PLANS[value] : null);

/**
 * The most an organisation's pool may be raised to from a request; a platform
 * admin can set more by hand. Well inside SES's daily quota either way.
 */
export const MAIL_PER_HOUR_MOST = 10_000;

/**
 * An organisation's hourly mail allowance: the platform admin's figure when
 * one is set, else its plan's. A plan this code does not know gets the
 * largest plan's, not "no limit": a mail pool with no ceiling is the one
 * thing the plans' grant-rather-than-lock rule must not reach
 * (cost-controls.md).
 */
export function mailAllowance(plan: Plan | null, override: number | null): number {
  if (override !== null && Number.isInteger(override) && override > 0) return override;
  return (plan ?? PLANS.business).mailPerHour;
}

/** An asked-for allowance checked: a whole number above the current one and not past `MAIL_PER_HOUR_MOST`. */
export function mailRequestProblem(wanted: unknown, current: number): string | null {
  if (typeof wanted !== "number" || !Number.isInteger(wanted))
    return "Ask for a whole number of mails an hour.";
  if (wanted <= current)
    return `Ask for more than the ${current.toLocaleString("en-US")} an hour you have now.`;
  if (wanted > MAIL_PER_HOUR_MOST)
    return `Ask for at most ${MAIL_PER_HOUR_MOST.toLocaleString("en-US")} an hour; for more, contact utuwrk.`;
  return null;
}

/** The cheapest plan with a feature, to name in a refusal. */
function cheapestWith(feature: PlanFeature): Plan {
  return PLAN_IDS.map((id) => PLANS[id]).find((plan) => plan.features.includes(feature)) ?? PLANS.business;
}

const WHAT: Record<PlanFeature, string> = {
  "simultaneous-work": "More than one onboarding at a time",
  "backup-assignees": "A backup",
  dashboards: "The overdue dashboard",
  hipaa: "The HIPAA flag",
  "multi-admin": "More than one admin",
};

/** Null when the plan has it (or there is no plan to go by); otherwise the sentence to show. */
export function featureProblem(plan: Plan | null, feature: PlanFeature): string | null {
  if (!plan || plan.features.includes(feature)) return null;
  return `${WHAT[feature]} is on the ${cheapestWith(feature).name} plan and above. This organisation is on ${plan.name}.`;
}

/** The next plan up that allows `count` of something, to name in a refusal. */
function planFor(count: number, limit: (plan: Plan) => number | null): Plan | null {
  return (
    PLAN_IDS.map((id) => PLANS[id]).find((plan) => {
      const most = limit(plan);
      return most === null || count <= most;
    }) ?? null
  );
}

function limitProblem(
  plan: Plan | null,
  count: number,
  limit: (plan: Plan) => number | null,
  noun: (n: number) => string,
): string | null {
  if (!plan) return null;
  const most = limit(plan);
  if (most === null || count <= most) return null;
  const next = planFor(count, limit);
  const nextRoom = next ? limit(next) : null;
  const then = !next
    ? ""
    : nextRoom === null
      ? ` ${next.name} has no limit.`
      : ` ${next.name} has room for ${noun(nextRoom)}.`;
  return `The ${plan.name} plan has room for ${noun(most)}.${then}`;
}

const counted = (one: string, many: string) => (n: number) =>
  `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

/**
 * Starting an onboarding: one more person under management, and -- on a plan
 * without simultaneous work -- nothing else in flight. `inFlight` is open
 * onboardings and offboardings alike (PRD 13.2: "1 active onboarding or
 * offboarding at a time"), though an offboarding itself is never refused.
 */
export function onboardingProblem(
  plan: Plan | null,
  input: { headcount: number; inFlight: number },
): string | null {
  if (!plan) return null;
  if (input.inFlight > 0 && !plan.features.includes("simultaneous-work"))
    return `The ${plan.name} plan runs one onboarding or offboarding at a time. Finish the one in progress, or move to ${cheapestWith("simultaneous-work").name}.`;
  return limitProblem(plan, input.headcount + 1, (each) => each.headcount, counted("person", "people"));
}

/** Adding a site, when the organisation has `sites` already. */
export const siteProblem = (plan: Plan | null, sites: number): string | null =>
  limitProblem(plan, sites + 1, (each) => each.sites, counted("site", "sites"));

/** Adding `adding` templates of its own, when the organisation has `templates` in use already. */
export const templateProblem = (plan: Plan | null, templates: number, adding = 1): string | null =>
  limitProblem(plan, templates + adding, (each) => each.templates, counted("template", "templates"));
